import { memo } from 'react';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/shadcn/hover-card';

import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import { pluralize } from '@/utils/strings';
import { getByPath, stringifyData } from '@/utils/widgets';
import type { WidgetData } from '../../widget.type';
import type { TextView } from '../text/text.view.type';
import type { ListCompactView } from './listCompact.type';

type Props = {
  view: ListCompactView;
  data: WidgetData;
};

const ListCompactViewImpl = memo(({ view, data }: Props) => {
  const { path, ordered, itemName = 'item' } = view;

  let listData = getByPath<string[] | string>(data, path);
  // if pointing at something that is not an array, build one with this only element
  if (listData !== undefined && !Array.isArray(listData)) {
    listData = [listData];
  }

  if (!listData?.length) return null;

  const nbItems = listData.length;

  // if only one item display it in plain text
  if (nbItems === 1)
    return <div className="truncate">{stringifyData(listData[0])}</div>;

  return (
    <div
      className="cursor-pointer truncate hover:text-primary"
      data-cy="list-view"
    >
      <HoverCard>
        <HoverCardTrigger>
          <div className="cursor-pointer truncate text-foreground hover:text-primary-deep">
            {pluralize(nbItems, itemName, false, true)}
          </div>
        </HoverCardTrigger>
        <HoverCardContent className="w-fit max-w-80">
          <div className="text-sm">
            {ordered ? (
              <ol className="list-inside list-decimal">
                {listData.map((listItem, index) => (
                  <li key={index}>{stringifyData(listItem)}</li>
                ))}
              </ol>
            ) : (
              <ul className="list-inside list-disc">
                {listData.map((listItem, index) => (
                  <li key={index}>{stringifyData(listItem)}</li>
                ))}
              </ul>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>
    </div>
  );
});

// sort defaults to text on path
const getSortConfig = (view: ListCompactView): TextView => ({
  type: 'text',
  path: view.path
});

// filtering defaults to text search on path
const getFilterConfig = (view: ListCompactView): TextView => ({
  type: 'text',
  path: view.path
});

// width defaults to 'medium'
const getDefaultWidth = (): ColumnWidth => 'medium';

// stringifies list data at path, join with ', '
const getStringifyFunction = (view: ListCompactView) => {
  const { path } = view;
  return data => {
    const listData = getByPath(data, path);
    if (listData === undefined) return '';

    if (!Array.isArray(listData)) {
      return stringifyData(listData);
    }

    return listData.map(item => stringifyData(item)).join(', ');
  };
};

export const listCompactViewProvider = {
  component: ListCompactViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
